import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { QBox, QLoader, QStack } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import type { Message } from '@captivateiq/assistant/types';
import { AgentMessage } from './AgentMessage';
import { ErrorMessage } from './ErrorMessage';
import { UserMessage } from './UserMessage';

import { TabLayout } from '../../TabLayout';
import { AgentIntroduction } from '../shared/AgentIntroduction';
import { MessageInput } from '../shared/MessageInput';

export const ChatView = () => {
  const bottomSentinelRef = useRef<HTMLDivElement>(null);

  const { selectedAgent, currentConversation, send, agentThinking, messageSendError } = useAIShellContext();

  const messages = useMemo(() => currentConversation?.messages || [], [currentConversation?.messages]);

  const sendAndScroll = useCallback(
    async (message: string) => {
      if (agentThinking) {
        return;
      }
      bottomSentinelRef.current?.scrollIntoView({ behavior: 'auto' });
      await send(message);
    },
    [send, agentThinking],
  );

  useEffect(() => {
    bottomSentinelRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages]);

  return (
    <TabLayout bottomContent={<MessageInput agentThinking={agentThinking} onSubmit={sendAndScroll} />}>
      <AgentIntroduction agentName={selectedAgent ?? ''} />
      <QStack
        direction="column"
        spacing="10px"
        sx={{
          alignItems: 'stretch',
          backgroundColor: 'grey.50',
          flex: 1,
          padding: '16px',
        }}
      >
        {messages.map((message: Message) => {
          switch (message.type) {
            case 'user':
              return <UserMessage key={message.contents} message={message} />;
            case 'agent':
              return <AgentMessage key={message.contents} message={message} />;
            default:
              return null;
          }
        })}
        {agentThinking && (
          <QBox
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <QLoader />
          </QBox>
        )}
        {messageSendError && <ErrorMessage message={messageSendError} />}
        <div ref={bottomSentinelRef} />
      </QStack>
    </TabLayout>
  );
};
